import { AnalyticAction, AnalyticCategories, AnalyticService } from '@application/analytic/analytic.service';
import { withTranslation } from '@application/i18n/with_translation';
import { BreakPoints, Button, DialogAlert, Flex, SafeArea, Text, useBreakpoint } from '@cellula/react';
import { primitiveColorTokens, tokens } from '@cellula/react-theme-patient';
import { LinkWithIcon } from '@components/footer/footer.component';
import { usePatientRepresentation } from '@feature/representation/use-patient-representation';
import { useRepresentablePatients } from '@feature/representation/use-representable-patients';
import { useFeatureFlag } from '@services/feature_flag/use_feature_flag';
import { StartupService } from '@services/startup.service';
import { useService } from '@visiba/instantiation';
import { Fragment } from 'react';
import styled from 'styled-components';
import { useCurrentLocation } from '../../application/use_current_location';
import { Logo } from '../logo.component';
import { CallToAction } from './_header_call_to_action.component';
import { Nav } from './_header_nav/mod';
import { NavigationMenu } from './_header_navigation_menu/mod';

interface Props {
	__TEMP__navigate?(url: string): void;
	__TEMP__onSignIn?: () => void;
	__TEMP__onSignOut?: () => void;
	__TEMP__changeLanguage?(langCode: string): void;
	/**
	 * @deprecated
	 */

	// biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration - should be fixed
	__TEMP__badgeCollector?: any;
}

const elements = {
	wrapper: styled.div`
		display: flex;
		flex-direction: column;
		width: 100%;
	`,
	container: styled.div`
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 0 ${tokens.spacing.x2};
		height: 100%;
		width: 100%;
	`,
	left: styled.div`
		display: flex;
		align-items: center;
		height: 100%;
	`,
	right: styled.div`
		position: relative;
	`,
	representationBanner: styled.div.attrs({ className: 'representation-banner' })`
		display: flex;
		align-items: center;
		background-color: ${primitiveColorTokens.primary['100']};
		box-sizing: border-box;
		min-height: 40px;

		${tokens.breakpointQuery.medium} {
			border-top: 1px solid ${tokens.color.border.brand};
			border-bottom: 1px solid ${tokens.color.border.brand};
		}
	`,
};

export function Header({ __TEMP__navigate, __TEMP__onSignIn, __TEMP__onSignOut, __TEMP__changeLanguage }: Props): JSX.Element {
	const analyticService = useService(AnalyticService);
	const startUpService = useService(StartupService);
	const breakpoints = useBreakpoint();
	const [unit, config] = useCurrentLocation();
	const featureUseRepresentatives = useFeatureFlag('PatientWeb.UseRepresentatives');
	const { currentlyRepresentedPatient } = useRepresentablePatients({ enabled: featureUseRepresentatives });
	const { stopRepresentation, isLoading } = usePatientRepresentation();

	function trackEvent(action: AnalyticAction): void {
		analyticService.trackEvent(AnalyticCategories.TopNavigation, action);
	}

	return (
		<elements.wrapper>
			<elements.container>
				{config != null ? (
					<Fragment>
						<elements.left>
							<Flex align='row' gap={tokens.spacing.x0_5}>
								<Logo
									trackEvent={trackEvent}
									__TEMP__navigate={__TEMP__navigate}
									logoUrl={(config.theme.EmailLogoUrl as string) ?? (config.theme.LogoUrl as string) ?? ''}
									locationName={unit?.name ?? ''}
								/>

								{__visiba_feature_flags__['PatientWeb.NHS.Navigation'] && startUpService.nhsInfo?.isInNHSApp && (
									<LinkWithIcon
										onClick={(e) => {
											e.preventDefault();
											startUpService.nhsInfo.nhsBackAction();
										}}
										icon='chevronLeft'
										target='_self'
										link={''}
									>
										{withTranslation('Component.TopBar.BackText')}
									</LinkWithIcon>
								)}
							</Flex>
							<Nav trackEvent={trackEvent} __TEMP__navigate={__TEMP__navigate} />
						</elements.left>

						<elements.right>
							{breakpoints === BreakPoints.Small ? (
								<NavigationMenu __TEMP__onSignIn={__TEMP__onSignIn} __TEMP__onSignOut={__TEMP__onSignOut} />
							) : (
								<CallToAction
									__TEMP__onSignIn={__TEMP__onSignIn}
									__TEMP__onSignOut={__TEMP__onSignOut}
									__TEMP__navigate={__TEMP__navigate}
									__TEMP__changeLanguage={__TEMP__changeLanguage}
									trackEvent={trackEvent}
								/>
							)}
						</elements.right>
					</Fragment>
				) : null}
			</elements.container>
			{currentlyRepresentedPatient && (
				<elements.representationBanner>
					<SafeArea maxWidth='632px' horizontalPadding={tokens.spacing.x2}>
						<Flex direction='row' gap={tokens.spacing.x1} align='center' justify='center' style={{ width: '100%' }}>
							<Text variant='labelSemiBold'>
								{withTranslation('PW.Component.TopBar.ActingForText', { name: currentlyRepresentedPatient.CommonName })}
							</Text>
							{isLoading ? (
								<Text variant='labelSemiBold'>{withTranslation('PW.Component.TopBar.StopRepresentingText')}</Text>
							) : (
								<DialogAlert
									header={withTranslation('PW.Component.TopBar.StopRepresentingDialog.Header')}
									description=''
									abortText={withTranslation('PW.Component.TopBar.StopRepresentingDialog.AbortText')}
									successText={withTranslation('PW.Component.TopBar.StopRepresentingDialog.SuccessText')}
									onSuccess={stopRepresentation}
									trigger={
										<Button variant='ghost' size='xSmall'>
											{withTranslation('PW.Component.TopBar.StopRepresentingText')}
										</Button>
									}
								/>
							)}
						</Flex>
					</SafeArea>
				</elements.representationBanner>
			)}
		</elements.wrapper>
	);
}
